import { useCallback } from "react";
import { format } from "date-fns";
import { Trans, useTranslation } from "react-i18next";
import { useAnalytics } from "contexts/AnalyticsContext";
import { useSalesData } from "contexts/SalesDataContext";
import { Skeleton } from "components/skeleton";
import { Text } from "components/text";
import { type Granularity, EmptyChart } from "components/charts";
import { useMarketSales } from "../../hooks/useMarketSales";
import type { MetadataEvent } from "../../hooks/useMetadataEvents";
import { useBundleFormatMeta } from "../../data/useBundleFormatMeta";
import { TotalSalesChart } from "./TotalSalesChart";
import { TotalSalesChartPanel } from "./TotalSalesChartPanel";

export const TotalSales = ({
  isbns,
  bookTitle,
  isBundled = false,
  bundleIsbns = [],
  workId,
  currentIsbn,
  isPublished,
  events,
}: {
  isbns: string[];
  bookTitle: string;
  isBundled?: boolean;
  bundleIsbns?: string[];
  workId?: string | null;
  currentIsbn?: string;
  isPublished?: boolean;
  events?: MetadataEvent[];
}) => {
  const { t } = useTranslation(["productDetails"]);
  const { gaEvent } = useAnalytics();
  const { markets, isFirstDatesLoading, publishedAt } = useSalesData();
  const { formats, isFormatsLoading } = useBundleFormatMeta({
    isBundled,
    workId,
    bundleIsbns,
    currentIsbn,
  });

  const queryIsbns = isBundled && bundleIsbns.length > 0 ? bundleIsbns : isbns;

  const {
    sales,
    isLoading,
    isError,
    granularity,
    setGranularity,
    availableGranularities,
  } = useMarketSales(queryIsbns);

  const handleGranularityChange = useCallback(
    (next: Granularity) => {
      setGranularity(next);
      gaEvent({
        type: "sales_total_sales_granularity",
        payload: { granularity: next, isbn: isbns[0] },
      });
    },
    [gaEvent, isbns, setGranularity],
  );

  if (isError) {
    return (
      <div className="rounded-xl border border-border bg-background p-6 text-center">
        <Text variant="metric">
          {t("productDetails:salesData.totalSales.error")}
        </Text>
      </div>
    );
  }

  // Hold the skeleton until sales-info resolves so the chart renders once in
  // its final market order (the market query runs in parallel). While bundled,
  // also wait for the formats: without them the mapper has nothing to stack by,
  // and the chart would flash the combined aggregate before snapping to the
  // per-format stacks.
  if (
    isLoading ||
    !sales ||
    isFirstDatesLoading ||
    (isBundled && isFormatsLoading)
  ) {
    return <Skeleton className="h-[360px] w-full rounded-xl" />;
  }

  if (sales.markets.length === 0 || sales.buckets.length === 0) {
    const prePublicationDate =
      publishedAt && !isPublished ? format(publishedAt, "d MMMM yyyy") : null;
    return (
      <TotalSalesChartPanel>
        <EmptyChart
          data-testid="total-sales-unavailable"
          title={t(
            prePublicationDate
              ? "salesData.totalSales.prePublication.title"
              : "salesData.empty.title",
          )}
          text={
            prePublicationDate ? (
              t("salesData.summaryCards.unavailable.notPublished", {
                title: bookTitle,
                date: prePublicationDate,
              })
            ) : (
              <Trans
                i18nKey="productDetails:salesData.empty.text"
                values={{ title: bookTitle }}
                components={{
                  bookTitle: (
                    <strong className="font-semibold text-secondary-700" />
                  ),
                }}
              />
            )
          }
        />
      </TotalSalesChartPanel>
    );
  }

  return (
    <TotalSalesChart
      sales={sales}
      marketOrder={markets}
      formats={formats}
      granularity={granularity}
      availableGranularities={availableGranularities}
      onGranularityChange={handleGranularityChange}
      events={events}
    />
  );
};
